import React from "react";
import { types } from "mobx-state-tree";
import { observer } from "mobx-react-lite";

import { MenuItems } from "../../../components/secondary-menu/SecondaryMenu";
import { translationService } from "../../../services/translation/TranslationService";
import { dateService } from "../../../services/date-service/DateService";
import {
  ViewSewerObject,
  ViewInspection,
  ViewSewerObjectFromAssignedProject,
  ViewInspectionFromAssignedProject,
} from "../../../services/authorization/Permissions";
import { SectionDetails } from "../../../services/sewer-object/SectionDetails";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";

export enum SectionDynamicMenuItemsPages {
  EditSection,
  EditInspection,
  ViewDamage,
  EditDamage,
  CreateDamageFromDamages,
  CreateDamageFromAnalysis,
}

export const SectionMenuItemsModel = types
  .model({
    sectionId: types.number,
    section: types.frozen<SectionDetails>(),
    dynamicMenuItemPage: types.maybe(types.frozen<SectionDynamicMenuItemsPages>()),
    inspectionId: types.maybe(types.number),
    damageId: types.maybe(types.number),
    parentIdFromPath: types.number,
    shouldDisableMenuItems: types.optional(types.boolean, false),
  })
  .actions((self) => {
    return {
      getStaticMenuItems(): MenuItems {
        return {
          "general.general": {
            items: [
              {
                id: "secondary-menu-sewer-objects-details",
                labelKey: "general.section.details",
                path: `/${self.parentIdFromPath}/sewer-objects/section/${self.sectionId}`,
                canAccess: permissionsService.userHasOneOfThePermissions([
                  ViewSewerObject,
                  ViewSewerObjectFromAssignedProject,
                ]),
                isActive: self.dynamicMenuItemPage === SectionDynamicMenuItemsPages.EditSection,
                disabled: self.shouldDisableMenuItems,
              },
              {
                id: "secondary-menu-section-map",
                labelKey: "general.sewer-objects.map",
                path: `/${self.parentIdFromPath}/sewer-objects/section/${self.sectionId}/map`,
                canAccess: permissionsService.userHasOneOfThePermissions([
                  ViewSewerObject,
                  ViewSewerObjectFromAssignedProject,
                ]),
                disabled: self.shouldDisableMenuItems,
              },
              {
                id: "secondary-menu-linked-objects",
                labelKey: "general.linked-objects",
                path: `/${self.parentIdFromPath}/sewer-objects/section/${self.sectionId}/linked-objects`,
                canAccess: permissionsService.userHasOneOfThePermissions([
                  ViewSewerObject,
                  ViewSewerObjectFromAssignedProject,
                ]),
                disabled: self.shouldDisableMenuItems,
              },
              {
                id: "secondary-menu-inspections",
                labelKey: "general.inspections",
                path: `/${self.parentIdFromPath}/sewer-objects/section/${self.sectionId}/inspections`,
                canAccess: permissionsService.userHasOneOfThePermissions([
                  ViewSewerObject,
                  ViewSewerObjectFromAssignedProject,
                ]),
                disabled: self.shouldDisableMenuItems,
              },
            ],
            defaultOpen: true,
          },
        };
      },
      getDynamicMenuItems(): MenuItems {
        const InspectionNameForMenuSection = observer(({ inspection }: any) => {
          return (
            <>
              <p>{`${translationService.translate("general.inspection")} ${
                inspection.inspectionNumber
              }`}</p>
              <p>{dateService.formatDate(inspection.gatheringDate || inspection.created)}</p>
            </>
          );
        });

        let inspectionMenuItems: MenuItems = {};
        self.section.inspectionsWithDates.forEach((inspection) => {
          inspectionMenuItems[`inspection-${inspection.inspectionNumber}`] = {
            sectionName: <InspectionNameForMenuSection inspection={inspection} />,
            items: [
              {
                id: "secondary-menu-inspections-details",
                labelKey: "general.inspections.details",
                path: `/${self.parentIdFromPath}/sewer-objects/section/${self.section.id}/inspections/${inspection.id}`,
                canAccess: permissionsService.userHasOneOfThePermissions([
                  ViewInspection,
                  ViewInspectionFromAssignedProject,
                ]),
                isActive:
                  inspection.id === self.inspectionId &&
                  self.dynamicMenuItemPage === SectionDynamicMenuItemsPages.EditInspection,
                disabled: self.shouldDisableMenuItems,
              },
              {
                id: "secondary-menu-files",
                labelKey: "general.files",
                path: `/${self.parentIdFromPath}/sewer-objects/section/${self.section.id}/inspections/${inspection.id}/files`,
                canAccess: permissionsService.userHasOneOfThePermissions([
                  ViewInspection,
                  ViewInspectionFromAssignedProject,
                ]),
                disabled: self.shouldDisableMenuItems,
              },
              {
                id: "secondary-menu-damages",
                labelKey: "general.damages",
                path: `/${self.parentIdFromPath}/sewer-objects/section/${self.section.id}/inspections/${inspection.id}/damages`,
                canAccess: permissionsService.userHasOneOfThePermissions([
                  ViewInspection,
                  ViewInspectionFromAssignedProject,
                ]),
                isActive:
                  inspection.id === self.inspectionId &&
                  (self.dynamicMenuItemPage === SectionDynamicMenuItemsPages.ViewDamage ||
                    self.dynamicMenuItemPage === SectionDynamicMenuItemsPages.EditDamage ||
                    self.dynamicMenuItemPage ===
                      SectionDynamicMenuItemsPages.CreateDamageFromDamages),
                disabled: self.shouldDisableMenuItems,
              },
              {
                id: "secondary-menu-analysis",
                labelKey: "general.analysis",
                path: `/${self.parentIdFromPath}/sewer-objects/section/${self.section.id}/inspections/${inspection.id}/analysis`,
                canAccess: permissionsService.userHasOneOfThePermissions([
                  ViewInspection,
                  ViewInspectionFromAssignedProject,
                ]),
                isActive:
                  inspection.id === self.inspectionId &&
                  self.dynamicMenuItemPage ===
                    SectionDynamicMenuItemsPages.CreateDamageFromAnalysis,
                disabled: self.shouldDisableMenuItems,
              },
              {
                id: "secondary-menu-report",
                labelKey: "general.report",
                path: `/${self.parentIdFromPath}/sewer-objects/section/${self.section.id}/inspections/${inspection.id}/report`,
                canAccess: permissionsService.userHasOneOfThePermissions([
                  ViewInspection,
                  ViewInspectionFromAssignedProject,
                ]),
                disabled: self.shouldDisableMenuItems,
              },
            ],
            defaultOpen: false,
          };
        });
        return inspectionMenuItems;
      },
    };
  });
